import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, ImagePlus, Link2, Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { api, mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { errorMessage } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { AMENITIES, amenityInfo } from '@/content/amenities';

// The customer-facing venue profile shown on the venue details page before
// booking (about, photos, facilities & amenities). Edited by the venue's own
// admin — the super_admin creates the venue with basic details but doesn't
// know what each venue has. Courts & sports aren't edited here: they're
// derived from the venue's courts. Saved via PUT /admin/academies/{id}/profile;
// photos are uploaded through POST /admin/media (or pasted as a link).
const MAX_PHOTOS = 10;
const MAX_UPLOAD_MB = 5;
const empty = { about: '', photos: [], amenities: [] };

export default function AdminVenueProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const venueId = user?.academy_id || '';
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [newPhoto, setNewPhoto] = useState('');
  const [customAmenity, setCustomAmenity] = useState('');

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    api.get(`/admin/academies/${venueId}/profile`)
      .then(({ data }) => setForm({ ...empty, ...data }))
      .catch((e) => toast({ title: 'Failed to load profile', description: errorMessage(e.response?.data?.detail), variant: 'error' }))
      .finally(() => setLoading(false));
  }, [venueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAmenity = (key) => setForm((f) => ({
    ...f,
    amenities: f.amenities.includes(key) ? f.amenities.filter((a) => a !== key) : [...f.amenities, key],
  }));

  const addCustomAmenity = () => {
    const v = customAmenity.trim();
    if (!v) return;
    if (!form.amenities.includes(v)) setForm((f) => ({ ...f, amenities: [...f.amenities, v] }));
    setCustomAmenity('');
  };

  const addPhotoLink = () => {
    const url = newPhoto.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast({ title: 'Invalid photo link', description: 'Use a link starting with https://', variant: 'error' });
      return;
    }
    setForm((f) => ({ ...f, photos: [...f.photos, url] }));
    setNewPhoto('');
  };

  const uploadFiles = async (fileList) => {
    const room = MAX_PHOTOS - form.photos.length;
    const files = Array.from(fileList || []).slice(0, Math.max(room, 0));
    if (fileList?.length > files.length) {
      toast({ title: `Only ${MAX_PHOTOS} photos allowed`, description: 'Extra files were skipped.', variant: 'error' });
    }
    for (const file of files) {
      if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast({ title: `${file.name} is too large`, description: `Photos must be ${MAX_UPLOAD_MB} MB or smaller.`, variant: 'error' });
        continue;
      }
      setUploading((n) => n + 1);
      try {
        const body = new FormData();
        body.append('file', file);
        const { data } = await api.post('/admin/media', body);
        setForm((f) => ({ ...f, photos: [...f.photos, data.url] }));
      } catch (e) {
        toast({ title: `Couldn't upload ${file.name}`, description: errorMessage(e.response?.data?.detail), variant: 'error' });
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const movePhotoToCover = (i) => setForm((f) => {
    const photos = [...f.photos];
    const [p] = photos.splice(i, 1);
    return { ...f, photos: [p, ...photos] };
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/academies/${venueId}/profile`, {
        about: form.about,
        photos: form.photos,
        amenities: form.amenities,
      });
      setForm({ ...empty, ...data });
      toast({ title: 'Venue profile saved', description: 'Customers see it on your venue page right away.', variant: 'success' });
    } catch (err) {
      toast({ title: 'Save failed', description: errorMessage(err.response?.data?.detail), variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const customAmenities = form.amenities.filter((a) => !AMENITIES.some((x) => x.key === a));

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-venue-profile">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-2">Venue profile</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">What customers see</h1>
        <p className="text-sm text-white/50 mt-2">
          Shown on your venue page before a customer books. Courts, sports, hours and prices come from Courts automatically.
        </p>
      </div>

      {!venueId ? (
        <div className="text-sm text-white/40">Your account has no venue assigned — contact the platform owner.</div>
      ) : loading ? (
        <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : (
        <form onSubmit={save} className="space-y-8">
          <Field label="About the venue" hint="A short description: courts, flooring, lighting, coaching, how to find you.">
            <Textarea
              value={form.about}
              onChange={(e) => setForm({ ...form, about: e.target.value })}
              maxLength={2000}
              className="min-h-[140px]"
              data-testid="profile-about"
            />
          </Field>

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
              Photos ({form.photos.length}/{MAX_PHOTOS}) — the first is the cover
            </div>
            {form.photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.photos.map((url, i) => (
                  <div key={`${url}-${i}`} className="relative border border-white/10 bg-surface aspect-video overflow-hidden">
                    <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
                    {i === 0 && <span className="absolute top-2 left-2 bg-volt text-black text-[10px] font-bold uppercase px-2 py-0.5">Cover</span>}
                    <div className="absolute top-2 right-2 flex gap-1">
                      {i > 0 && (
                        <button type="button" onClick={() => movePhotoToCover(i)} className="bg-black/70 text-[10px] uppercase px-2 py-1 hover:text-volt">
                          Make cover
                        </button>
                      )}
                      <a href={mediaUrl(url)} target="_blank" rel="noreferrer" aria-label="Open photo" className="bg-black/70 p-1 hover:text-volt"><ExternalLink size={13} /></a>
                      <button
                        type="button"
                        aria-label="Remove photo"
                        onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }))}
                        className="bg-black/70 p-1 hover:text-[#FF3B30]"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {form.photos.length < MAX_PHOTOS && (
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadFiles(e.target.files)}
                  data-testid="profile-photo-file"
                />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading > 0}>
                  {uploading > 0
                    ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                    : <><ImagePlus size={14} /> Upload photos</>}
                </Button>
                <div className="text-[11px] text-white/40">JPEG, PNG or WebP, up to {MAX_UPLOAD_MB} MB each. Or paste an image link:</div>
                <div className="flex gap-2">
                  <Input
                    value={newPhoto}
                    onChange={(e) => setNewPhoto(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPhotoLink(); } }}
                    placeholder="https://… image link"
                    data-testid="profile-photo-url"
                  />
                  <Button type="button" variant="outline" onClick={addPhotoLink}><Link2 size={14} /> Add</Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">Facilities &amp; amenities</div>
            <div className="flex flex-wrap gap-2">
              {AMENITIES.map((a) => {
                const on = form.amenities.includes(a.key);
                const Icon = a.icon;
                return (
                  <button
                    type="button"
                    key={a.key}
                    onClick={() => toggleAmenity(a.key)}
                    data-testid={`amenity-${a.key}`}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm border transition-colors ${on ? 'border-volt bg-volt/10 text-white' : 'border-white/10 text-white/50 hover:border-white/30'}`}
                  >
                    <Icon size={14} className={on ? 'text-volt' : ''} /> {a.label}
                  </button>
                );
              })}
              {customAmenities.map((key) => (
                <span key={key} className="flex items-center gap-1.5 px-3 py-2 text-sm border border-volt bg-volt/10">
                  {amenityInfo(key).label}
                  <button type="button" aria-label={`Remove ${key}`} onClick={() => toggleAmenity(key)} className="hover:text-[#FF3B30]"><X size={13} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={customAmenity}
                maxLength={40}
                onChange={(e) => setCustomAmenity(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomAmenity(); } }}
                placeholder="Something else? e.g. Juice bar, Pro shop"
                data-testid="profile-custom-amenity"
              />
              <Button type="button" variant="outline" onClick={addCustomAmenity}><Plus size={14} /> Add</Button>
            </div>
          </div>

          <Button type="submit" disabled={saving || uploading > 0} data-testid="profile-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving</> : <><Save size={14} /> Save profile</>}
          </Button>
        </form>
      )}
    </div>
  );
}
